import React from 'react';
import { Box, Button, Typography, Card, CardContent, Stack, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)<{ isPopular?: boolean; isCustom?: boolean }>(({ theme, isPopular, isCustom }) => ({
    position: 'relative',
    height: '450px', // 카드 높이 더 줄임
    borderRadius: isPopular ? '0 0 16px 16px' : '16px',
    border: isPopular ? '2px solid #FF4B6B' : '1px solid #E5E7EB',
    borderTop: isPopular ? 'none' : '1px solid #E5E7EB',
    background: isCustom ? '#F8F9FA' : '#FFFFFF',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    },
    [theme.breakpoints.down('md')]: {
        height: '440px', // 모바일에서도 높이 더 줄임
    },
}));

const PriceText = styled(Typography)(({ theme }) => ({
    fontSize: '48px',
    fontWeight: 700,
    color: '#1F2937',
    lineHeight: 1,
}));

const FeatureList = styled(Stack)(({ theme }) => ({
    gap: '12px',
    marginTop: '16px', // marginTop 줄임
}));

const FeatureItem = styled(Box)<{ isIncluded?: boolean; isAddOn?: boolean }>(
    ({ isIncluded = true, isAddOn = false }) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        color: isIncluded ? '#374151' : '#9CA3AF',
        fontSize: '14px',
        lineHeight: '20px',
    }),
);

interface ActionButtonProps {
    buttonvariant: 'primary' | 'secondary' | 'tertiary';
    ispopular?: boolean;
    isdisabled?: boolean;
}

const ActionButton = styled(Button)<ActionButtonProps>(({ theme, buttonvariant, ispopular, isdisabled }) => {
    const styles = {
        primary: {
            backgroundColor: '#FF4B6B',
            color: 'white',
            '&:hover': {
                backgroundColor: '#E03E5A',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(255, 75, 107, 0.4)',
            },
        },
        secondary: {
            backgroundColor: ispopular ? '#FF4B6B' : '#374151',
            color: 'white',
            '&:hover': {
                backgroundColor: ispopular ? '#E03E5A' : '#1F2937',
                transform: 'translateY(-1px)',
                boxShadow: ispopular ? '0 4px 12px rgba(255, 75, 107, 0.4)' : '0 4px 12px rgba(55, 65, 81, 0.4)',
            },
        },
        tertiary: {
            backgroundColor: 'transparent',
            color: '#374151',
            border: '2px solid #D1D5DB',
            '&:hover': {
                backgroundColor: '#F9FAFB',
                borderColor: '#9CA3AF',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            },
        },
    };

    const baseStyles = {
        ...styles[buttonvariant],
        borderRadius: '12px',
        padding: '16px 24px',
        fontSize: '16px',
        fontWeight: 600,
        textTransform: 'none' as const,
        width: '100%',
        minHeight: '50px',
        transition: 'all 0.2s ease-in-out',
        boxShadow: buttonvariant === 'tertiary' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
    };

    // 비활성화된 상태에서는 색상은 유지하되 hover 효과와 클릭 효과만 제거
    if (isdisabled) {
        return {
            ...baseStyles,
            '&:hover': {
                backgroundColor: baseStyles.backgroundColor,
                transform: 'none',
                boxShadow: baseStyles.boxShadow,
            },
            '&:active': {
                transform: 'none',
            },
            cursor: 'not-allowed',
            opacity: 0.6,
        };
    }

    return baseStyles;
});

export interface PricingPlan {
    id: string;
    title: string;
    description: string;
    price: number | 'Free' | 'Custom';
    period?: string;
    isPopular?: boolean;
    features: Array<{
        text: string;
        isIncluded: boolean;
        isAddOn?: boolean;
    }>;
    buttonText: string;
    buttonVariant: 'primary' | 'secondary' | 'tertiary';
}

interface PricingCardProps {
    plan: PricingPlan;
    onSubscribe: (planId: string) => void;
}

interface ExtendedPricingCardProps extends PricingCardProps {
    disabled?: boolean;
    isCurrentPlan?: boolean;
    isDowngrade?: boolean;
}

const PricingCard: React.FC<ExtendedPricingCardProps> = ({
    plan,
    onSubscribe,
    disabled = false,
    isCurrentPlan = false,
    isDowngrade = false,
}) => {
    const navigate = useNavigate();

    const handleButtonClick = () => {
        if (isCurrentPlan) {
            // 현재 플랜인 경우 계정설정 페이지의 구독 및 결제 탭으로 이동
            navigate('/account/settings#billing');
        } else if (isDowngrade) {
            // 다운그레이드인 경우 클릭 방지
            return;
        } else {
            // 새로운 구독인 경우 기존 로직 실행
            onSubscribe(plan.id);
        }
    };
    const renderPrice = () => {
        if (plan.price === 'Free') {
            return (
                <Box display="flex" alignItems="baseline" gap={1}>
                    <PriceText>무료</PriceText>
                    <Typography color="text.secondary" fontSize="14px">
                        / 업로드 5회
                    </Typography>
                </Box>
            );
        }

        if (plan.price === 'Custom') {
            return (
                <Box display="flex" alignItems="baseline" gap={1}>
                    <PriceText>맞춤형</PriceText>
                    <Typography color="text.secondary" fontSize="14px">
                        / 월
                    </Typography>
                </Box>
            );
        }

        return (
            <Box display="flex" alignItems="baseline" gap={1} sx={{ whiteSpace: 'nowrap' }}>
                <PriceText>{plan.price.toLocaleString()}</PriceText>
                <Typography fontSize="20px" color="text.secondary">
                    원
                </Typography>
                <Typography color="text.secondary" fontSize="14px">
                    {plan.period}
                </Typography>
            </Box>
        );
    };

    const renderFeatureIcon = (feature: PricingPlan['features'][0]) => {
        if (feature.isAddOn) {
            return (
                <AddIcon
                    sx={{
                        fontSize: 16,
                        color: '#FF4B6B',
                        marginTop: '2px',
                    }}
                />
            );
        }

        return (
            <CheckIcon
                sx={{
                    fontSize: 16,
                    color: feature.isIncluded ? '#10B981' : '#E5E7EB',
                    marginTop: '2px',
                }}
            />
        );
    };

    return (
        <StyledCard isPopular={plan.isPopular} isCustom={plan.price === 'Custom'}>
            <CardContent
                sx={{
                    padding: '32px 32px 80px 32px', // 하단에 버튼 공간 확보
                    height: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: '20px',
                            fontWeight: 600,
                            color: '#1F2937',
                            marginBottom: '8px',
                        }}
                    >
                        {plan.title}
                    </Typography>

                    <Typography
                        color="text.secondary"
                        sx={{
                            fontSize: '14px',
                            marginBottom: '24px',
                            lineHeight: '20px',
                        }}
                    >
                        {plan.description}
                    </Typography>

                    {renderPrice()}
                </Box>

                <FeatureList>
                    {plan.features.map((feature, index) => (
                        <FeatureItem key={index} isIncluded={feature.isIncluded} isAddOn={feature.isAddOn}>
                            {renderFeatureIcon(feature)}
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: feature.isIncluded ? '#374151' : '#9CA3AF',
                                    lineHeight: '20px',
                                }}
                            >
                                {feature.text}
                            </Typography>
                        </FeatureItem>
                    ))}
                </FeatureList>

                {/* 버튼을 절대 위치로 하단에 고정 */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '32px',
                        left: '32px',
                        right: '32px',
                    }}
                >
                    <ActionButton
                        buttonvariant={plan.buttonVariant}
                        ispopular={plan.isPopular}
                        isdisabled={isDowngrade}
                        onClick={handleButtonClick}
                        disabled={false}
                    >
                        {isCurrentPlan ? '구독중' : plan.buttonText}
                    </ActionButton>
                </Box>
            </CardContent>
        </StyledCard>
    );
};

export default PricingCard;
